<template>
    <div class="modal fade" :id="id" data-bs-backdrop="static" aria-labelledby="support-modal-dynamic" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="d-flex align-items-start w-100">
                        <div class="flex-grow-1" style="min-width: 0; margin-right: 1rem;">
                            <h3 class="mt-2 mb-2 text-center text-sm-start">
                                <img src="/img/images/lockstep-status-bar-icon.png" alt="Lockstep Status Bar Icon"
                                    class="lockstep-status-bar-icon ms-0" style="width: 40px; height: 40px;">
                                LockStep Support Request
                            </h3>
                        </div>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal"
                                data-bs-toggle="tooltip" title="Close">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body">
                    <div class="row">
                        <p class="mb-2">If you have a request or run into something in Avid that doesn't work as
                            expected, we would love to hear about it!
                            When you use the form below, we will also capture a screenshot of the page, the URL, and
                            additional details to help us troubleshoot.</p>
                        <p class="mb-3">For general usage questions, please visit our <a
                                href="https://help.avidai.com/hc/en-us" target="_blank">Knowledge Base</a> for answers
                            to common questions.</p>
                        <div class="col-12 col-sm-4 mb-3">
                            <label class="form-label">Type of Request</label>
                            <select class="form-select" v-model="form.category" @change="errors.category = ''"
                                :class="{ 'is-invalid': errors.category !== '' }" aria-label="Type of Request">
                                <option value="">** Please Select **</option>
                                <option value="BILLING_ISSUE">Billing / Pricing</option>
                                <option value="FEATURE_REQUEST">Feature Request</option>
                                <option value="GENERAL_INQUIRY">General Question</option>
                                <option value="PRODUCT_ISSUE">Report a Bug</option>
                                <option value="SETUP_HELP">Setup Assistance</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.category !== ''">
                                {{ errors.category }}
                            </div>
                        </div>
                        <div class="col-12 col-sm-8 mb-3">
                            <label class="form-label">Level of Urgency</label>
                            <select class="form-select" v-model="form.urgency" @change="errors.urgency = ''"
                                :class="{ 'is-invalid': errors.urgency !== '' }" aria-label="Level of Urgency">
                                <option value="LOW" v-if="form.category === 'PRODUCT_ISSUE'">Low - "Something looks off"
                                </option>
                                <option value="LOW" v-else>Low - "Nothing urgent but wanted you to know"</option>
                                <option value="MEDIUM" v-if="form.category === 'PRODUCT_ISSUE'">Medium - "Something's
                                    broken but I can work around it."</option>
                                <option value="MEDIUM" v-else>Medium - "Can you get back to me in 1-2 business days?"
                                </option>
                                <option value="HIGH" v-if="form.category === 'PRODUCT_ISSUE'">High - "It's broken and I
                                    need it fixed ASAP."</option>
                                <option value="HIGH" v-else>High - "I need a response ASAP"</option>
                            </select>
                            <div class="text-danger invalid-feedback" v-if="errors.urgency !== ''">
                                {{ errors.urgency }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label">Quick Summary</label>
                            <input type="text" class="form-control" v-model="form.title" @change="errors.title = ''"
                                :class="{ 'is-invalid': errors.title !== '' }" :placeholder="summaryPlaceholder" aria-label="Quick Summary">
                            <div class="text-danger invalid-feedback" v-if="errors.title !== ''">
                                {{ errors.title }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label">
                                <span v-if="form.category === 'PRODUCT_ISSUE'">Description of the Issue</span>
                                <span v-else-if="form.category === 'FEATURE_REQUEST'">Description of the Idea</span>
                                <span v-else>More Details</span>
                            </label>
                            <textarea class="form-control full-height" style="min-height: 80px;"
                                v-model="form.description" @change="errors.description = ''"
                                :class="{ 'is-invalid': errors.description !== '' }" :placeholder="descriptionPlaceholder"></textarea>
                            <div class="text-danger invalid-feedback" v-if="errors.description !== ''">
                                {{ errors.description }}
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col mb-3">
                            <label class="form-label">Supporting Documents (Additional screenshots or files to help us troubleshoot)</label>
                            <div class="d-flex flex-wrap gap-2 align-items-start">
                                <!-- Screenshot loading placeholder -->
                                <div v-if="capturingScreenshot" class="position-relative image-thumbnail-wrapper"
                                    style="max-width: 200px;">
                                    <div class="d-flex flex-column align-items-center justify-content-center image-thumbnail"
                                        style="height: 100px; width: 100px; border-radius: 20px; background-color: #f8f9fa; border: 1px solid #dee2e6;">
                                        <div class="spinner-border spinner-border-sm text-primary" role="status"
                                            style="width: 24px; height: 24px;">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Display uploaded files as thumbnails -->
                                <div v-for="(file, file_index) in getValidFiles()" :key="file_index"
                                    class="position-relative image-thumbnail-wrapper" style="max-width: 200px;">
                                    <!-- Image thumbnail for image files -->
                                    <img v-if="isImageFile(file)" :src="getFileUrl(file)" alt="Uploaded image"
                                        class="image-thumbnail"
                                        style="height: 100px; width: auto; max-width: 100%; object-fit: contain; border-radius: 20px; cursor: pointer;"
                                        @click.prevent="openFilePreview(file_index)" />
                                    <!-- File icon for non-image files -->
                                    <div v-else
                                        class="d-flex flex-column align-items-center justify-content-center image-thumbnail"
                                        style="height: 100px; width: 100px; border-radius: 20px; background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 8px;">
                                        <img src="/img/icons/avid-icons/file-text.svg" alt="File"
                                            style="width: 40px; height: 40px; margin-bottom: 4px;">
                                        <div class="text-truncate w-100 text-center"
                                            style="font-size: 11px; line-height: 1.2; max-width: 100%;"
                                            :data-bs-toggle="getFileName(file) ? 'tooltip' : ''"
                                            :title="getFileName(file) || ''">
                                            {{ getFileName(file) }}
                                        </div>
                                    </div>
                                    <!-- Regenerate button for first image (screenshot) -->
                                    <div v-if="file_index === 0 && isScreenshot(file)" class="position-absolute top-0 end-0 d-flex gap-1 m-1">
                                        <button type="button" class="btn btn-icon-only-sm btn-secondary p-1"
                                            data-bs-toggle="tooltip" title="Regenerate Screenshot"
                                            @click.stop.prevent="regenerateScreenshot()" :disabled="working || capturingScreenshot"
                                            style="display: flex; align-items: center; justify-content: center;">
                                            <img class="icon" src="/img/icons/avid-icons/refresh.svg" v-if="!capturingScreenshot" alt="">
                                            <div v-else class="spinner-border spinner-border-sm" role="status" style="width: 16px; height: 16px;">
                                                <span class="visually-hidden">Loading...</span>
                                            </div>
                                        </button>
                                    </div>
                                    <!-- Delete button for other files -->
                                    <div v-else-if="file_index !== 0" class="position-absolute top-0 end-0 d-flex gap-1 m-1">
                                        <button type="button" class="btn btn-icon-only-sm btn-secondary p-1"
                                            data-bs-toggle="tooltip" title="Remove"
                                            @click.stop.prevent="removeFile(file_index)" :disabled="working"
                                            style="display: flex; align-items: center; justify-content: center;">
                                            <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                        </button>
                                    </div>
                                </div>

                                <!-- Plus button to add new file -->
                                <button type="button"
                                    class="btn btn-secondary d-flex align-items-center justify-content-center add-image-btn"
                                    data-bs-toggle="tooltip" title="Add Attachment" @click.prevent="openFileModal()"
                                    :disabled="working" style="width: 100px; height: 100px; border-radius: 20px;">
                                    <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add Attachment"
                                        style="width: 24px; height: 24px;">
                                </button>
                            </div>

                            <!-- Image Upload Modal - Teleported to body for nested modal support -->
                            <Teleport to="body">
                                <div class="modal fade" id="support-image-upload-modal" tabindex="-1"
                                    aria-labelledby="support-image-upload-modal-label" aria-hidden="true"
                                    data-bs-backdrop="static">
                                    <div class="modal-dialog modal-dialog-centered">
                                        <div class="modal-content">
                                            <div class="modal-header border-bottom-0 pb-0">
                                                <h5 class="modal-title" id="support-image-upload-modal-label">Upload
                                                    File</h5>
                                                <button type="button" class="btn btn-white close-modal"
                                                    data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
                                                    <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                                </button>
                                            </div>
                                            <div class="modal-body">
                                                <ImageDropzone :key="'support-image-dropzone-' + modalImageResetKey"
                                                    v-model="modalFileValue" class="image-dropzone-wrapper"
                                                    id="modal-support-image-dropzone" :disabled="working"
                                                    :if_thumbnail="false" :acceptAllFiles="true"
                                                    :emitFileMetadata="true"
                                                    @update:modelValue="handleModalFileUpdate" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Image Preview Modal - Teleported to body for nested modal support -->
                                <div class="modal fade" id="support-image-preview-modal" tabindex="-1"
                                    aria-labelledby="support-image-preview-modal-label" aria-hidden="true"
                                    data-bs-backdrop="static">
                                    <div class="modal-dialog modal-dialog-centered modal-lg">
                                        <div class="modal-content">
                                            <div class="modal-header border-bottom-0 pb-0">
                                                <h5 class="modal-title" id="support-image-preview-modal-label">Image
                                                    Preview</h5>
                                                <button type="button" class="btn btn-white close-modal"
                                                    data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close"
                                                    @click="closeFilePreviewModal">
                                                    <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
                                                </button>
                                            </div>
                                            <div class="modal-body px-4 pb-4">
                                                <!-- Image Display -->
                                                <div v-if="previewFile && isImageFile(previewFile)"
                                                    class="d-flex justify-content-center align-items-center mb-4"
                                                    style="min-height: 400px;">
                                                    <img :src="getFileUrl(previewFile)" alt="Preview image"
                                                        class="img-fluid"
                                                        style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 20px;" />
                                                </div>
                                            </div>
                                            <div class="modal-footer pt-1">
                                                <div class="w-100 d-flex justify-content-end gap-2">
                                                    <!-- Regenerate button for screenshot (first image) -->
                                                    <button v-if="previewFileIndex === 0 && isScreenshot(previewFile)" type="button" class="btn btn-secondary"
                                                        @click.prevent="regenerateScreenshotFromPreview()" :disabled="working || capturingScreenshot">
                                                        <img v-if="!capturingScreenshot" class="icon me-2" src="/img/icons/avid-icons/refresh.svg" alt="">
                                                        <div v-else class="d-inline-block spinner-border spinner-border-sm me-2" role="status">
                                                            <span class="visually-hidden">Loading...</span>
                                                        </div>
                                                        Regenerate
                                                    </button>
                                                    <!-- Delete button for other files -->
                                                    <button v-else type="button" class="btn btn-secondary"
                                                        @click.prevent="deletePreviewFile()" :disabled="working">
                                                        <img class="icon me-2" src="/img/icons/avid-icons/trash.svg" alt="">
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Teleport>
                        </div>
                    </div>
                    <div class="row" v-if="form.category === 'PRODUCT_ISSUE'">
                        <div class="col mb-3">
                            <!-- Add expandable section for diagnostic information -->
                            <div class="accordion" id="diagnostic-accordion">
                                <div class="accordion-item">
                                    <h2 class="accordion-header" id="headingOne">
                                        <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
                                            Diagnostic Information
                                        </button>
                                    </h2>
                                    <div id="collapseOne" class="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#diagnostic-accordion">
                                        <div class="accordion-body">

                                            <p class="mb-1">URL: {{ diagnostics.url }}</p>
                                            <p class="mb-1">Previous Route: {{ diagnostics.previousRoute }}</p>
                                            <p class="mb-1">User: {{ diagnostics.user.id }} - {{ diagnostics.user.email }} - org={{ diagnostics.user.organization }}</p>
                                            <p class="mb-1">Device: {{ diagnostics.device.os }} - {{ diagnostics.device.browser }} - {{ diagnostics.device.viewport }}</p>
                                            <p class="mb-1">Environment: {{ diagnostics.build.env }}</p>
                                            <p class="mb-1">Network: {{ diagnostics.network.failed }} - {{ diagnostics.network.avgLatencyMs }}</p>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row footer">
                        <div class="col-12 mb-3 d-flex justify-content-end">
                            <button class="btn btn-primary w-100 w-sm-auto" @click="submitTicket()" :disabled="working">
                                <span v-if="working">
                                    <div class="d-inline-block spinner-border spinner-border-sm me-2" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div> Working...
                                </span>
                                <span v-else>
                                    <img src="/img/icons/avid-icons/mail-02.svg" class="icon me-2" alt=""> Submit
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import html2canvas from 'html2canvas';
import ImageDropzone from './ImageDropzone.vue';

export default {
    name: 'SupportModal',
    components: {
        ImageDropzone
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            default: null
        },
        selected_ticket_type: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default: 'support-modal-dynamic'
        }
    },

    data() {
        return {
            form: {
                category: '',
                urgency: '',
                title: '',
                description: '',
                images: [] // Array of objects: {url: string, type: string, name: string} or legacy string URLs
            },
            errors: {
                category: '',
                title: '',
                description: '',
                urgency: ''
            },
            diagnostics: {
                url: "https://app.avidai.com/reports/123",
                previousRoute: "/reports",
                user: {"id": "usr_123", "email": "evan@avidai.com", "clientId": 42},
                device: {"os": "macOS 15", "browser": "Chrome 129", "viewport": "1512x982@2x"},
                build: { "env": "prod" },
                network: {"failed": 1, "avgLatencyMs": 420},
                errors: []
            },
            working: false,
            modalFileValue: null,
            modalImageResetKey: 0,
            previewFile: null,
            previewFileIndex: null,
            capturingScreenshot: false
        };
    },

    watch: {
        selected_ticket_type: {
            immediate: true,
            handler(newVal) {
                if (newVal !== '') {
                    this.form.category = newVal;
                }
            }
        }
    },

    computed: {
        summaryPlaceholder() {
            if (this.form.category === 'GENERAL_INQUIRY') {
                return 'Quick Summary: What can we help you with?';
            }
            if (this.form.category === 'PRODUCT_ISSUE') {
                return 'Quick Summary: What broke? e.g., "Export fails on /reports"';
            }
            if (this.form.category === 'FEATURE_REQUEST') {
                return 'Quick Summary: What would you like to see?';
            }
            if (this.form.category === 'BILLING_ISSUE') {
                return 'Quick Summary: Billing or pricing question';
            }
            if (this.form.category === 'SETUP_HELP') {
                return 'Quick Summary: What are you trying to set up?';
            }
            return '';
        },
        descriptionPlaceholder() {
            if (this.form.category === 'GENERAL_INQUIRY') {
                return 'Provide additional information that can help us answer your question quickly.';
            }
            if (this.form.category === 'PRODUCT_ISSUE') {
                return 'Expected vs actual, steps to reproduce, frequency, and impact.';
            }
            if (this.form.category === 'FEATURE_REQUEST') {
                return 'Problem you’re solving, who benefits, desired outcome, priority, examples.';
            }
            if (this.form.category === 'BILLING_ISSUE') {
                return 'Invoice #, plan, seat count, dates, expected vs charged.';
            }
            if (this.form.category === 'SETUP_HELP') {
                return 'Environment, steps taken, errors, deadline/impact.';
            }
            return '';
        }
    },

    methods: {
        captureScreenshot() {
            const snapshotElement = document.getElementById('snapshot-content');
            if (!snapshotElement) {
                return;
            }

            this.capturingScreenshot = true;

            html2canvas(snapshotElement).then(canvas => {
                const imageDataUrl = canvas.toDataURL("image/png");
                const screenshotData = {
                    url: imageDataUrl,
                    type: "image/png",
                    name: "screenshot.png"
                };

                // Add screenshot as first item in images array
                if (!Array.isArray(this.form.images)) {
                    this.form.images = [];
                }

                // Remove any existing screenshot (in case modal is reopened)
                this.form.images = this.form.images.filter(file => {
                    if (typeof file === 'object' && file.name === 'screenshot.png') {
                        return false;
                    }
                    if (typeof file === 'string' && file.startsWith('data:image/png')) {
                        return false;
                    }
                    return true;
                });

                // Add screenshot as first item
                this.form.images.unshift(screenshotData);
                this.capturingScreenshot = false;
            }).catch(error => {
                console.error('Error capturing screenshot:', error);
                this.capturingScreenshot = false;
            });
        },
        submitTicket() {
            this.errors = {
                category: '',
                title: '',
                description: '',
                urgency: ''
            };

            if (this.form.category === '') {
                this.errors.category = 'Please select a category';
            }
            if (this.form.title === '') {
                this.errors.title = 'Please provide a title';
            }
            if (this.form.description === '') {
                this.errors.description = 'Please provide a description';
            }
            if (this.form.urgency === '') {
                this.errors.urgency = 'Please select a level of urgency';
            }

            if (this.errors.category === '' && this.errors.title === '' &&
                this.errors.description === '' && this.errors.urgency === '') {
                this.working = true;
                this.postForm();
            }
        },

        getValidFiles() {
            if (!Array.isArray(this.form.images)) {
                return [];
            }
            return this.form.images.filter(file => {
                if (file === null || file === '' || file === undefined) {
                    return false;
                }
                // Handle legacy string URLs - convert to object format
                if (typeof file === 'string') {
                    return true;
                }
                // Handle object format
                return file && file.url;
            });
        },
        isImageFile(file) {
            if (typeof file === 'string') {
                // Legacy string URL - assume it's an image if it has image extension
                return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(file);
            }
            if (file && file.type) {
                return file.type.startsWith('image/');
            }
            return false;
        },
        isScreenshot(file) {
            if (typeof file === 'string') {
                // Legacy string URL - check if it's a data URL (screenshot)
                return file.startsWith('data:image/');
            }
            if (file && file.name) {
                return file.name === 'screenshot.png';
            }
            return false;
        },
        getFileUrl(file) {
            if (typeof file === 'string') {
                return file;
            }
            return file.url || '';
        },
        getFileName(file) {
            if (typeof file === 'string') {
                // Try to extract filename from URL
                const urlParts = file.split('/');
                return urlParts[urlParts.length - 1].split('?')[0];
            }
            return file.name || '';
        },
        openFileModal() {
            this.modalFileValue = null;
            this.modalImageResetKey += 1;

            this.$nextTick(() => {
                const modalElement = document.getElementById('support-image-upload-modal');
                if (modalElement) {
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }

                    const modal = new window.bootstrap.Modal(modalElement, {
                        backdrop: 'static',
                        keyboard: false,
                        focus: true
                    });

                    const handleHidden = () => {
                        this.modalFileValue = null;
                        modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                    };
                    modalElement.addEventListener('hidden.bs.modal', handleHidden);

                    modal.show();

                    this.$nextTick(() => {
                        const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                            if (existingTooltip) {
                                existingTooltip.dispose();
                            }
                            new window.bootstrap.Tooltip(tooltipTriggerEl);
                        });
                    });
                }
            });
        },
        handleModalFileUpdate(value) {
            if (value) {
                let fileData;

                // Handle object format from ImageDropzone with emitFileMetadata
                if (typeof value === 'object' && value.url) {
                    // Normalize URL
                    let url = value.url.replace("%2F", "/");
                    url = url.startsWith("http") ? url : 'https:' + url;
                    fileData = {
                        url: url,
                        type: value.type || '',
                        name: value.name || ''
                    };
                }
                // Handle legacy string format (for backward compatibility)
                else if (typeof value === 'string' && value !== '') {
                    let url = value.replace("%2F", "/");
                    url = url.startsWith("http") ? url : 'https:' + url;
                    fileData = url; // Keep as string for backward compatibility
                } else {
                    return; // Invalid value
                }

                if (!Array.isArray(this.form.images)) {
                    this.form.images = [];
                }

                this.form.images.push(fileData);

                const modalElement = document.getElementById('support-image-upload-modal');
                if (modalElement) {
                    const modal = window.bootstrap.Modal.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    }
                }

                this.modalFileValue = null;
                this.modalImageResetKey += 1;

                // Reinitialize tooltips for newly added file thumbnails
                this.$nextTick(() => {
                    this.initTooltips();
                });
            }
        },
        initTooltips() {
            const tooltipTriggerList = document.querySelectorAll(`#${this.id} [data-bs-toggle="tooltip"]`);
            [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                // Dispose existing tooltip if it exists
                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                if (existingTooltip) {
                    existingTooltip.dispose();
                }
                // Initialize new tooltip
                new window.bootstrap.Tooltip(tooltipTriggerEl);
            });
        },
        removeFile(file_index) {
            const validFiles = this.getValidFiles();
            if (Array.isArray(this.form.images) && validFiles.length > 0) {
                let validIndex = 0;
                for (let i = 0; i < this.form.images.length; i++) {
                    const file = this.form.images[i];
                    if (file !== null && file !== '' && file !== undefined) {
                        if (validIndex === file_index) {
                            this.form.images.splice(i, 1);
                            break;
                        }
                        validIndex++;
                    }
                }

                if (this.form.images.length === 0) {
                    this.form.images = [];
                }
            }
        },
        openFilePreview(file_index) {
            const validFiles = this.getValidFiles();
            if (validFiles.length > 0 && file_index < validFiles.length) {
                const file = validFiles[file_index];
                // Only open preview for image files
                if (!this.isImageFile(file)) {
                    return;
                }
                this.previewFile = file;
                this.previewFileIndex = file_index;

                this.$nextTick(() => {
                    const modalElement = document.getElementById('support-image-preview-modal');
                    if (modalElement) {
                        const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                        if (existingModal) {
                            existingModal.dispose();
                        }

                        const modal = new window.bootstrap.Modal(modalElement, {
                            backdrop: 'static',
                            keyboard: false,
                            focus: true
                        });

                        const handleHidden = () => {
                            this.closeFilePreviewModal();
                            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                        };
                        modalElement.addEventListener('hidden.bs.modal', handleHidden);

                        modal.show();

                        this.$nextTick(() => {
                            const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                            [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }
                                new window.bootstrap.Tooltip(tooltipTriggerEl);
                            });
                        });
                    }
                });
            }
        },
        deletePreviewFile() {
            if (this.previewFileIndex !== null) {
                this.removeFile(this.previewFileIndex);
                this.closeFilePreviewModal();
            }
        },
        closeFilePreviewModal() {
            this.previewFile = null;
            this.previewFileIndex = null;

            const modalElement = document.getElementById('support-image-preview-modal');
            if (modalElement) {
                const modal = window.bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
            }
        },
        regenerateScreenshot() {
            // Remove the current screenshot (first item)
            if (Array.isArray(this.form.images) && this.form.images.length > 0) {
                const firstItem = this.form.images[0];
                if (this.isScreenshot(firstItem)) {
                    this.form.images.shift(); // Remove first item
                }
            }
            // Capture new screenshot
            this.captureScreenshot();
        },
        regenerateScreenshotFromPreview() {
            // Close the preview modal first
            this.closeFilePreviewModal();
            // Regenerate the screenshot
            this.regenerateScreenshot();
        },

        postForm() {
            this.form.url = window.location.href;
            if (this.client !== null) {
                this.form.client_id = this.client.id;
            }
            this.form.user_id = this.user.id;
            this.form.email = this.user.email;

            // Extract screenshot (first item if it's a data URL) and send as form.image for backward compatibility
            let screenshotDataUrl = null;
            if (Array.isArray(this.form.images) && this.form.images.length > 0) {
                const firstItem = this.form.images[0];
                // Check if first item is the screenshot (data URL)
                if (typeof firstItem === 'string' && firstItem.startsWith('data:image/')) {
                    screenshotDataUrl = firstItem;
                } else if (typeof firstItem === 'object' && firstItem.url && firstItem.url.startsWith('data:image/')) {
                    screenshotDataUrl = firstItem.url;
                }
            }

            // Set form.image if we found a screenshot
            if (screenshotDataUrl) {
                this.form.image = screenshotDataUrl;
            }

            // Filter out null/empty values and normalize file data before sending
            if (Array.isArray(this.form.images)) {
                this.form.images = this.form.images
                    .filter(file => {
                        if (file === null || file === '' || file === undefined) {
                            return false;
                        }
                        // Handle object format
                        if (typeof file === 'object') {
                            return file.url && file.url !== '';
                        }
                        // Handle legacy string format
                        return typeof file === 'string' && file !== '';
                    })
                    .map(file => {
                        // Normalize to object format for API
                        if (typeof file === 'string') {
                            return {
                                url: file,
                                type: this.isImageFile(file) ? 'image/jpeg' : 'application/octet-stream',
                                name: ''
                            };
                        }
                        return file;
                    });
            }

            // Add diagnostics to form data
            this.form.diagnostics = this.diagnostics;

            window.axios.post('/api/support', this.form)
                .then(response => {
                    this.working = false;
                    this.form = {
                        category: '',
                        urgency: '',
                        title: '',
                        description: '',
                        images: []
                    };
                    this.errors = {
                        category: '',
                        title: '',
                        description: '',
                        urgency: ''
                    };
                    this.modalFileValue = null;
                    this.modalImageResetKey = 0;
                    this.previewFile = null;
                    this.previewFileIndex = null;
                    this.capturingScreenshot = false;

                    const myModalEl = document.getElementById(this.id);
                    const modal = bootstrap.Modal.getInstance(myModalEl);
                    modal.hide();

                    Swal.fire({
                        title: 'Support Ticket Submitted',
                        text: 'Your request has been received. Your account manager will be reaching out to you shortly to collect additional information and discuss next steps. We\'re sorry about the inconvenience!',
                        icon: "success",
                        confirmButtonText: 'OK',
                        customClass: {
                            popup: 'swal-success'
                        },
                    });
                })
                .catch(error => {
                    this.working = false;
                    Swal.fire({
                        title: 'Error',
                        html: 'This is embarrassing! We ran into an error submitting your error. We\'ve created a standalone page for just this situation. <a target="_blank" href="https://help.avidai.com/hc/en-us/requests/new?ticket_form_id=42423067497747">Please visit this link to try again.</a>',
                        icon: "error",
                        confirmButtonText: 'OK',
                        customClass: {
                            popup: 'swal-error'
                        },
                    }).then(() => {
                        const myModalEl = document.getElementById(this.id);
                        const modal = bootstrap.Modal.getInstance(myModalEl);
                        modal.hide();
                    });
                });
        },

        generateDiagnostics() {
            // Get URL
            this.diagnostics.url = window.location.href;
            
            // Get previous route from history state or document referrer
            this.diagnostics.previousRoute = window.history.state?.back || document.referrer || 'N/A';
            
            // Get user info from prop
            this.diagnostics.user = this.user;
            
            // Get device information from browser
            const ua = navigator.userAgent;
            const os = this.getOS(ua);
            const browser = this.getBrowser(ua);
            const viewport = `${window.innerWidth}x${window.innerHeight}${window.devicePixelRatio > 1 ? '@' + window.devicePixelRatio + 'x' : ''}`;
            this.diagnostics.device = {
                os: os,
                browser: browser,
                viewport: viewport
            };
            
            // Get build information from window properties
            this.diagnostics.build = {
                env: window.app_env || 'N/A'
            };

            // Get network information (if available from performance API)
            const networkInfo = this.getNetworkInfo();
            this.diagnostics.network = {
                failed: networkInfo.failed || 0,
                avgLatencyMs: networkInfo.avgLatencyMs || 0
            };
            
            // Get recent Sentry errors from error buffer
            this.diagnostics.errors = (window.avidErrorBuffer || []).slice(); // Copy array to avoid reference issues

            console.log('Diagnostics:', this.diagnostics);
        },
        
        getOS(userAgent) {
            if (userAgent.includes('Win')) return 'Windows';
            if (userAgent.includes('Mac')) return 'macOS';
            if (userAgent.includes('Linux')) return 'Linux';
            if (userAgent.includes('Android')) return 'Android';
            if (userAgent.includes('iOS')) return 'iOS';
            return 'Unknown';
        },
        
        getBrowser(userAgent) {
            if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
            if (userAgent.includes('Firefox')) return 'Firefox';
            if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
            if (userAgent.includes('Edg')) return 'Edge';
            return 'Unknown';
        },
        
        getNetworkInfo() {
            // Try to get network information from Performance API
            try {
                const navigation = performance.getEntriesByType('navigation')[0];
                if (navigation) {
                    const failed = navigation.transferSize === 0 && navigation.duration > 0 ? 1 : 0;
                    const latency = navigation.responseStart - navigation.requestStart;
                    return {
                        failed: failed,
                        avgLatencyMs: Math.round(latency)
                    };
                }
            } catch (e) {
                console.warn('Could not get network info from Performance API:', e);
            }
            return { failed: 0, avgLatencyMs: 0 };
        },
        
        handleModalShown() {
            console.log('Modal shown');
            this.generateDiagnostics();
            // Small delay to ensure modal is fully rendered before capturing screenshot
            this.$nextTick(() => {
                setTimeout(() => {
                    this.captureScreenshot();
                }, 100);
            });
        }

    },
    mounted() {
        // Initialize Bootstrap tooltips
        this.$nextTick(() => {
            this.initTooltips();
        });

        // Listen for modal show event
        const modalElement = document.getElementById(this.id);
        if (modalElement) {
            modalElement.addEventListener('shown.bs.modal', this.handleModalShown);
        }
    },
    beforeUnmount() {
        const previewModalElement = document.getElementById('support-image-preview-modal');
        if (previewModalElement) {
            const previewModal = window.bootstrap.Modal.getInstance(previewModalElement);
            if (previewModal) {
                previewModal.hide();
                previewModal.dispose();
            }
        }
        
        // Remove event listener
        const modalElement = document.getElementById(this.id);
        if (modalElement) {
            modalElement.removeEventListener('shown.bs.modal', this.handleModalShown);
        }
    }
}
</script>